import { useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import LedgerTable, { computeResult } from "../components/LedgerTable";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Loader2, RotateCcw } from "lucide-react";
import { useDirty, useDirtyGuard, useUnsavedChangesWarning } from "../context/DirtyContext";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";

function emptyRow() {
    return {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        karat_type: "",
        weight: "",
        karat: "",
        description: "",
        date: "",
    };
}

function sumGold(rows) {
    return rows.reduce((s, r) => {
        const v = parseFloat(computeResult(r));
        return isFinite(v) ? s + v : s;
    }, 0);
}

function isRowFilled(r) {
    return [r.karat_type, r.weight, r.karat, r.description, r.date].some((v) => (v || "").trim());
}

export default function ClientLedger() {
    const [clientName, setClientName] = useState("");
    const [creditRows, setCreditRows] = useState([emptyRow()]);
    const [debitRows, setDebitRows] = useState([emptyRow()]);
    const [saving, setSaving] = useState(false);
    const { guardedRun } = useDirty();
    const { t } = useLang();
    const { company } = useAuth();
    const karatLock = company?.karat_lock || null;

    const goldCredit = useMemo(() => sumGold(creditRows), [creditRows]);
    const goldDebit = useMemo(() => sumGold(debitRows), [debitRows]);
    const netGold = goldCredit - goldDebit;
    const positiveGold = netGold > 0;
    const negativeGold = netGold < 0;

    const isDirty =
        clientName.trim() !== "" ||
        creditRows.some(isRowFilled) ||
        debitRows.some(isRowFilled);

    const reset = () => {
        setClientName("");
        setCreditRows([emptyRow()]);
        setDebitRows([emptyRow()]);
    };

    const save = async () => {
        if (!clientName.trim()) {
            toast.error(t.toastNeedClient);
            return false;
        }
        const fc = creditRows.filter(isRowFilled);
        const fd = debitRows.filter(isRowFilled);
        if (fc.length === 0 && fd.length === 0) {
            toast.error(t.toastEmptyLedger);
            return false;
        }
        setSaving(true);
        try {
            await api.post("/ledgers", {
                kind: "client",
                client_name: clientName.trim(),
                credit_rows: fc,
                debit_rows: fd,
                total_credit_gold: goldCredit,
                total_debit_gold: goldDebit,
                net_gold: netGold,
            });
            toast.success(t.toastSaved);
            reset();
            return true;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || t.toastSaveFail);
            return false;
        } finally {
            setSaving(false);
        }
    };

    useDirtyGuard({ isDirty, save, title: t.saveDataTitle });
    useUnsavedChangesWarning(isDirty);

    const onResetClick = () => guardedRun(reset);

    return (
        <AppShell>
            <div className="mb-4 flex flex-col items-stretch justify-between gap-3 sm:mb-6 sm:flex-row sm:items-end sm:gap-4">
                <div>
                    <button
                        type="button"
                        onClick={() => guardedRun(() => window.history.length > 1 ? window.history.back() : null)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                        data-testid="back-to-picker"
                    >
                        ← {t.backToPicker}
                    </button>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.ledgerKicker}</p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.clientLedgerTitle}</h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.ledgerSubtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={onResetClick}
                        className="flex-1 sm:flex-none"
                        data-testid="ledger-reset-btn"
                    >
                        <RotateCcw className="ms-1 h-4 w-4" />
                        {t.ledgerEmpty}
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="flex-1 bg-neutral-900 text-white hover:bg-neutral-800 sm:flex-none"
                        data-testid="ledger-save-btn"
                    >
                        {saving ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <Save className="ms-1 h-4 w-4" />}
                        {t.ledgerSaveBtn}
                    </Button>
                </div>
            </div>

            <div className="paper-surface rounded-2xl border border-neutral-200 p-3 shadow-sm sm:rounded-3xl sm:p-5 lg:p-8">
                {/* Client name */}
                <div className="mb-4 flex flex-col gap-3 border-b border-dashed border-neutral-200 pb-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between sm:pb-6">
                    <div className="flex-1">
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500 sm:mb-1.5 sm:text-xs">
                            {t.clientNameLabel}
                        </label>
                        <input
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            placeholder={t.clientNamePlaceholder}
                            className="w-full border-0 border-b-2 border-neutral-300 bg-transparent px-0 pb-2 font-heading text-2xl font-bold text-neutral-900 outline-none transition focus:border-amber-600 sm:text-3xl lg:text-4xl"
                            data-testid="client-name-input"
                        />
                    </div>
                    <div className="text-end text-xs text-neutral-500">
                        <div className="font-mono text-sm font-semibold text-neutral-700">
                            {new Date().toLocaleDateString("en-GB")}
                        </div>
                        <div>{t.pageDate}</div>
                    </div>
                </div>

                <div className="space-y-6 sm:space-y-8">
                    <LedgerTable
                        title={t.creditTitle}
                        rows={creditRows}
                        onChange={setCreditRows}
                        variant="credit"
                        kind="client"
                        testIdPrefix="client-credit"
                        karatLock={karatLock}
                    />
                    <LedgerTable
                        title={t.debitTitle}
                        rows={debitRows}
                        onChange={setDebitRows}
                        variant="debit"
                        kind="client"
                        testIdPrefix="client-debit"
                        karatLock={karatLock}
                    />
                </div>

                {/* Net Gold only — no Cash card for Client Ledger */}
                <div className="mt-6 sm:mt-8">
                    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5" data-testid="client-net-gold-card">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-800 ring-1 ring-amber-200 sm:px-3 sm:text-xs">
                                    {t.goldChip}
                                </div>
                                <div className="mt-1.5 text-xs text-neutral-500 sm:mt-2">{t.netGoldLabel}</div>
                            </div>
                            <div
                                className={`rounded-xl px-4 py-2 font-numbers text-2xl font-bold sm:px-5 sm:py-3 sm:text-3xl ${
                                    positiveGold
                                        ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                                        : negativeGold
                                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                        : "bg-neutral-50 text-neutral-500 ring-1 ring-neutral-200"
                                }`}
                                data-testid="client-net-gold"
                            >
                                {netGold === 0
                                    ? "0"
                                    : (positiveGold ? "+" : "") +
                                      netGold.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-4 border-t border-neutral-100 pt-3 text-sm sm:gap-6">
                            <div>
                                <span className="text-neutral-500">{t.forLabel}</span>{" "}
                                <span className="font-numbers font-semibold text-red-700" data-testid="client-gold-credit-total">
                                    {goldCredit.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                </span>
                            </div>
                            <div>
                                <span className="text-neutral-500">{t.againstLabel}</span>{" "}
                                <span className="font-numbers font-semibold text-emerald-700" data-testid="client-gold-debit-total">
                                    {goldDebit.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
